import React, { useCallback, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import Dropdown from "../../components/common/Dropdown";
import FormGroup from "../../components/common/FormGroup";
import { getActivityPCVTypes, resolveTypeName, ActivityPCVType } from "../../api/activityPCVType";
import ActivityPCVTypesModal from "./ActivityPCVTypesModal";
import { ReactComponent as SettingsIcon } from "../../assets/images/settings-4-line.svg";
import styles from "./ActivityTypeField.module.css";

const CREATE_NEW_VALUE = "__create_new_activity_type__";

interface ActivityTypeFieldProps {
    /** Formik field name holding the selected type id. */
    name?: string;
    /** Title rendered by the surrounding FormGroup. */
    title?: string;
    /** Subtitle rendered by the surrounding FormGroup. */
    subtitle?: string;
    /** Whether the field is required (shows the asterisk + validation error). */
    required?: boolean;
}

/**
 * A select for the activity's type, with an option to create a new type and a
 * gear icon (in the field header) that opens the company type management modal.
 * Formik-aware.
 */
const ActivityTypeField: React.FC<ActivityTypeFieldProps> = ({
    name = "activityPCVTypeId",
    title,
    subtitle,
    required = false,
}) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { values, setFieldValue } = useFormikContext<Record<string, any>>();
    const token = localStorage.getItem("token") || "";

    const [types, setTypes] = useState<ActivityPCVType[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const fetchTypes = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await getActivityPCVTypes({ token });
            setTypes(Array.isArray(response) ? response : []);
        } catch (err) {
            console.error("Error fetching activity types:", err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchTypes();
    }, [fetchTypes]);

    const options = [
        ...types.map((type) => ({
            value: type.id,
            label: resolveTypeName(type, i18n.language) || t("unnamed"),
        })),
        { value: CREATE_NEW_VALUE, label: `+ ${t("createNewActivityType")}` },
    ];

    const handleChange = (value?: string) => {
        if (value === CREATE_NEW_VALUE) {
            setModalOpen(true);
            return;
        }
        setFieldValue(name, value || null);
    };

    const gearAction = (
        <Tooltip title={t("manageActivityTypes")} arrow>
            <button
                type="button"
                onClick={() => setModalOpen(true)}
                aria-label={t("manageActivityTypes")}
                className={styles.gearBtn}
            >
                <SettingsIcon style={{ width: 16, height: 16 }} />
            </button>
        </Tooltip>
    );

    return (
        <FormGroup name={name} title={title} subtitle={subtitle} required={required} headerAction={gearAction}>
            <Dropdown
                label=""
                options={options}
                value={values[name] || ""}
                onChange={handleChange}
                loading={loading}
                error={false}
                placeholder={t("selectActivityType")}
                name={name}
                isWithoutMargin
                fullWidth
            />
            <ActivityPCVTypesModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onChanged={fetchTypes} />
        </FormGroup>
    );
};

export default ActivityTypeField;
